import type { ReactNode } from "react";

import { Text } from "react-aria-components";

import { useUIVersion } from "#hooks";
import { NegativeSolidIcon } from "#icons";
import { cn } from "#utils";

export interface FormFieldAssistiveTextProps {
  /** The content to display as assistive text. When falsy, nothing is rendered. */
  children?: ReactNode;
  /** Whether the associated field is in an invalid/error state */
  isInvalid?: boolean;
  /** Whether the associated field is disabled */
  isDisabled?: boolean;
  /** Additional CSS class names */
  className?: string;
  /**
   * Id for the rendered text. Set this when the field cannot pick the text up
   * through the `description` slot and needs to reference it via
   * `aria-describedby` instead.
   */
  id?: string;
}

/**
 * FormFieldAssistiveText is a wrapper component for displaying help text or error messages
 * below form fields. When in an invalid state, it displays an error icon to the left
 * of the text.
 *
 * Accessibility:
 * - Uses React Aria's Text component with slot="description" to automatically
 *   link the text to the parent field via aria-describedby
 * - The error icon is decorative (aria-hidden="true") since the text already
 *   conveys the error state to screen readers
 * - When isInvalid is true, the field's aria-invalid attribute (managed by React Aria)
 *   will announce the error state to assistive technologies
 */
export function FormFieldAssistiveText({
  children,
  isInvalid = false,
  isDisabled = false,
  className,
  id,
}: FormFieldAssistiveTextProps) {
  const uiVersion = useUIVersion();

  if (!children) {
    return null;
  }

  return (
    <Text
      className={cn(
        "flex items-center gap-x-xxs text-label-small text-text-secondary",
        uiVersion === "v1" ? "text-legacy-navy-L30" : undefined,
        isDisabled ? "text-text-disabled" : undefined,
        isInvalid ? "text-text-negative" : undefined,
        className,
      )}
      id={id}
      slot="description"
    >
      {isInvalid ? <NegativeSolidIcon aria-hidden="true" className="shrink-0" size={16} /> : null}
      {children}
    </Text>
  );
}
